import React, { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { IonText } from "@ionic/react";
import "./MieGraphScreen.css";
import { useBLE } from "../../../hooks/useBle";
import { TherapyParameters } from "../../../contexts/types";
import { IS_WEB_BUILD } from "../../../utils/constants";
// import { useBLE } from "../../Context/bleContext";
// Define our therapy phases
export enum TherapyPhase {
  PAUSE = "pause",
  INSPIRATORY = "inspiratory",
  EXPIRATORY = "expiratory",
  INACTIVE = "inactive",
}

interface WaveGraphProps {
  isConnected: boolean;
  therapyActive: boolean;
  currentPhase: TherapyPhase;
  phaseProgress: number;
  therapyParams: TherapyParameters;
  width: number;
  height: number;
}

// Updated PressureDisplayProps interface to include cycleNumber
interface PressureDisplayProps {
  currentPhase: TherapyPhase;
  therapyParams: TherapyParameters;
}

// Create a new component for the cycle counter
interface CycleCounterProps {
  cycleNumber: number;
}

const CycleCounter: React.FC<CycleCounterProps> = ({ cycleNumber }) => {
  return (
    <motion.div
      className="cycle-counter"
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: 0.2, duration: 0.3 }}
    >
      <div className="cycle-value">{cycleNumber}</div>
      <div className="cycle-label">Cycles</div>
    </motion.div>
  );
};

// Define the CircularTimer component for phase time indication
interface CircularTimerProps {
  progress: number; // 0 to 1
  isActive: boolean;
  color: string;
  size?: number;
  strokeWidth?: number;
  label: string;
  duration: number; // in milliseconds
}

const CircularTimer: React.FC<CircularTimerProps> = ({
  progress,
  isActive,
  color,
  size = 20,
  strokeWidth = 4,
  label,
  duration,
}) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference * (1 - progress);

  // Calculate remaining seconds
  const remainingSeconds = (((1 - progress) * duration) / 1000).toFixed(1);

  return (
    <div className={`circular-timer ${isActive ? "active" : ""}`}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="transparent"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeOpacity={isActive ? 0.3 : 0.1}
        />

        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="transparent"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          strokeOpacity={isActive ? 1 : 0.3}
        />

        {/* Center point */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={strokeWidth / 2}
          fill={color}
          opacity={isActive ? 1 : 0.3}
        />
      </svg>
      <div
        className="timer-label"
        style={{ color: color, opacity: isActive ? 1 : 0.5 }}
      >
        <div className="timer-phase">{label}</div>
        <div className="timer-seconds">
          {isActive ? remainingSeconds : (duration / 1000).toFixed(1)}s
        </div>
      </div>
    </div>
  );
};

// Modified PressureDisplay component with smoother transitions
const PressureDisplay: React.FC<PressureDisplayProps> = ({
  currentPhase,
  therapyParams,
}) => {
  // Using a key based on the current phase to force clean re-render and avoid animation conflicts
  return (
    <div className="pressure-display-container">
      <motion.div
        className="pressure-displays"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.3 }}
      >
        {therapyParams.enabledPhases.includes(TherapyPhase.PAUSE) && (
          <motion.div
            key={`pause-${currentPhase === TherapyPhase.PAUSE}`} // Add key for clean render
            className={`pressure-box pause ${
              currentPhase === TherapyPhase.PAUSE ? "active" : ""
            }`}
            initial={{
              opacity: currentPhase === TherapyPhase.PAUSE ? 1 : 0.8,
              scale: currentPhase === TherapyPhase.PAUSE ? 1.03 : 1,
            }}
            animate={{
              scale: currentPhase === TherapyPhase.PAUSE ? 1.03 : 1,
              opacity: currentPhase === TherapyPhase.PAUSE ? 1 : 0.8,
            }}
            transition={{
              type: "tween", // Change to tween for more direct animation
              duration: 0.15, // Faster transition
              ease: "easeOut", // Smoother easing
            }}
          >
            <div className="pressure-label">Pause Pressure</div>
            <div className="pressure-value">{therapyParams.pausePressure}</div>
            <div className="pressure-unit">cm H2O</div>
          </motion.div>
        )}

        {therapyParams.enabledPhases.includes(TherapyPhase.INSPIRATORY) && (
          <motion.div
            key={`insp-${currentPhase === TherapyPhase.INSPIRATORY}`} // Add key for clean render
            className={`pressure-box insp ${
              currentPhase === TherapyPhase.INSPIRATORY ? "active" : ""
            }`}
            initial={{
              opacity: currentPhase === TherapyPhase.INSPIRATORY ? 1 : 0.8,
              scale: currentPhase === TherapyPhase.INSPIRATORY ? 1.03 : 1,
            }}
            animate={{
              scale: currentPhase === TherapyPhase.INSPIRATORY ? 1.03 : 1,
              opacity: currentPhase === TherapyPhase.INSPIRATORY ? 1 : 0.8,
            }}
            transition={{
              type: "tween", // Change to tween for more direct animation
              duration: 0.15, // Faster transition
              ease: "easeOut", // Smoother easing
            }}
          >
            <div className="pressure-label">Insp. Pressure</div>
            <div className="pressure-value">{therapyParams.inspPressure}</div>
            <div className="pressure-unit">cm H2O</div>
          </motion.div>
        )}

        {therapyParams.enabledPhases.includes(TherapyPhase.EXPIRATORY) && (
          <motion.div
            key={`exp-${currentPhase === TherapyPhase.EXPIRATORY}`} // Add key for clean render
            className={`pressure-box exp ${
              currentPhase === TherapyPhase.EXPIRATORY ? "active" : ""
            }`}
            initial={{
              opacity: currentPhase === TherapyPhase.EXPIRATORY ? 1 : 0.8,
              scale: currentPhase === TherapyPhase.EXPIRATORY ? 1.03 : 1,
            }}
            animate={{
              scale: currentPhase === TherapyPhase.EXPIRATORY ? 1.03 : 1,
              opacity: currentPhase === TherapyPhase.EXPIRATORY ? 1 : 0.8,
            }}
            transition={{
              type: "tween", // Change to tween for more direct animation
              duration: 0.15, // Faster transition
              ease: "easeOut", // Smoother easing
            }}
          >
            <div className="pressure-label">Exp. Pressure</div>
            <div className="pressure-value">{therapyParams.expPressure}</div>
            <div className="pressure-unit">cm H2O</div>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
};

// Modified WaveGraph component with improved circular indicator
const WaveGraph: React.FC<WaveGraphProps> = ({
  isConnected,
  therapyActive,
  currentPhase,
  phaseProgress,
  therapyParams,
  width,
  height,
}) => {
  // Add ref to track previous phase
  const prevPhaseRef = useRef<TherapyPhase | null>(null);
  // Add state to control indicator visibility during phase transitions
  const [showIndicator, setShowIndicator] = useState(true);

  const graphContainerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  // Setup dimensions
  useEffect(() => {
    const updateDimensions = () => {
      if (graphContainerRef.current) {
        setDimensions({
          width: width,
          height: height,
        });
      }
    };

    updateDimensions();
    window.addEventListener("resize", updateDimensions);

    return () => {
      window.removeEventListener("resize", updateDimensions);
    };
  }, []);

  // First effect: Track phase changes specifically
  useEffect(() => {
    // Only run this when currentPhase changes
    if (
      prevPhaseRef.current !== null &&
      prevPhaseRef.current !== currentPhase
    ) {
      console.log(
        "Phase changed from",
        prevPhaseRef.current,
        "to",
        currentPhase
      );

      // Handle cycle reset (when we go back to the first phase from the last)
      const firstPhase = therapyParams.enabledPhases[0];
      const lastPhase =
        therapyParams.enabledPhases[therapyParams.enabledPhases.length - 1];

      console.log("enabled phases:", therapyParams.enabledPhases);
      console.log("first phase:", firstPhase, "last phase:", lastPhase);

      if (prevPhaseRef.current === lastPhase && currentPhase === firstPhase) {
        console.log("Detected cycle reset - hiding indicator");
        // Hide the indicator during transition
        setShowIndicator(false);

        // // Show it again after a brief delay
        // const timer = setTimeout(() => {
        //   console.log('Showing indicator again after delay');
        //   setShowIndicator(true);
        // }, 50);

        // // Clean up timer
        // return () => clearTimeout(timer);
      }
    }

    // Always update the previous phase reference
    prevPhaseRef.current = currentPhase;
  }, [currentPhase, therapyParams.enabledPhases]); // Remove phaseProgress from dependencies

  // Second effect: Force indicator to update position on phase progress changes
  // This keeps the indicator moving smoothly even if we don't need to hide/show it
  useEffect(() => {
    // Make sure indicator is shown during normal phase progression
    // We only need to set this if it's currently hidden
    if (
      !showIndicator &&
      prevPhaseRef.current === currentPhase && // Same phase
      phaseProgress > 0.05
    ) {
      // Not at the very beginning
      setShowIndicator(true);
    }
  }, [phaseProgress, showIndicator, currentPhase]);
  // Function to calculate adaptive pressure range with padding
  const calculatePressureRange = () => {
    // Get all enabled phase pressures
    const pressures: number[] = [];

    if (therapyParams.enabledPhases.includes(TherapyPhase.PAUSE)) {
      pressures.push(therapyParams.pausePressure);
    }

    if (therapyParams.enabledPhases.includes(TherapyPhase.INSPIRATORY)) {
      pressures.push(therapyParams.inspPressure);
    }

    if (therapyParams.enabledPhases.includes(TherapyPhase.EXPIRATORY)) {
      pressures.push(therapyParams.expPressure);
    }

    // If no pressures, use default range
    if (pressures.length === 0) {
      return { min: -30, max: 30 };
    }

    // Calculate min and max with padding
    const minPressure = Math.min(...pressures);
    const maxPressure = Math.max(...pressures);
    const range = maxPressure - minPressure;

    // Add padding (15%)
    const padding = Math.max(5, range * 0.15);

    // Calculate adjusted min and max to ensure they're multiples of 5
    // Always include 0 in the range
    const adjustedMin = Math.min(
      0,
      Math.floor((minPressure - padding) / 5) * 5
    );
    const adjustedMax = Math.max(0, Math.ceil((maxPressure + padding) / 5) * 5);

    return {
      min: Math.max(-90, adjustedMin),
      max: Math.min(90, adjustedMax),
    };
  };

  // Calculate the pressure range once for this render
  const pressureRange = calculatePressureRange();

  // Define the colors for each phase
  const phaseColors = {
    pause: "#66bb6a", // Green for pause phase
    inspiratory: "#29b6f6", // Blue for inspiratory phase
    expiratory: "#ffa726", // Yellow/orange for expiratory phase
  };

  // Define muted colors for inactive state preview
  const mutedPhaseColors = {
    pause: "#4a8c4d", // Muted green for pause phase
    inspiratory: "#1e7ca9", // Muted blue for inspiratory phase
    expiratory: "#b27a1d", // Muted orange for expiratory phase
  };

  // Updated getWaveformSegments function with sine wave implementation
  const getWaveformSegments = () => {
    const padding = 20;
    const graphWidth = dimensions.width - padding * 2;
    const graphHeight = dimensions.height - padding * 2;

    // If no phases are enabled or dimensions not set, return empty array
    if (therapyParams.enabledPhases.length === 0 || dimensions.width === 0) {
      return [];
    }

    // Use the calculated pressure range for normalization
    const minPressure = pressureRange.min;
    const maxPressure = pressureRange.max;
    const rangeMagnitude = maxPressure - minPressure;

    const normalizeY = (pressure: number) => {
      // Invert Y so higher pressure is higher on the graph
      return (
        dimensions.height -
        padding -
        ((pressure - minPressure) / rangeMagnitude) * graphHeight
      );
    };

    const segments = [];
    let currentX = padding;

    // Revert to original sine wave pattern but maintain consistent wavelength
    const generateSineWavePath = (
      startX: number,
      endX: number,
      y: number,
      phase: string
    ) => {
      // Calculate wave parameters
      const segmentWidth = endX - startX;

      // Define fixed wavelength in pixels
      const fixedWavelength = 15; // 30 pixels per complete wave cycle

      // Calculate how many complete waves we can fit
      const completeCycles = Math.floor(segmentWidth / fixedWavelength);

      // If segment is too small for even one complete wave, draw a straight line
      if (completeCycles < 1) {
        return `M ${startX},${y} L ${endX},${y}`;
      }

      // Define amplitude as a percentage of the graph height (small but noticeable)
      const amplitude = graphHeight * 0.025; // 2.5% of graph height

      // Adjust the actual segment we'll draw waves in to ensure we only draw complete waves
      const adjustedWidth = completeCycles * fixedWavelength;
      const remainingSpace = segmentWidth - adjustedWidth;

      // Center the waves within the available space by adding padding on both sides
      const startPadding = remainingSpace / 2;
      const adjustedStartX = startX + startPadding;

      // Calculate points along the sine wave
      // Use high point density for smooth curves (20 points per wave)
      const pointsPerCycle = 20;
      const pointCount = completeCycles * pointsPerCycle;

      const points = [];

      // Always start exactly at the baseline for smooth transition from vertical line
      points.push(`${startX},${y}`);

      // Generate the sine wave points
      for (let i = 0; i <= pointCount; i++) {
        const t = i / pointCount; // Normalized position (0 to 1)
        const x = adjustedStartX + adjustedWidth * t;
        // Calculate sine wave y position - use regular sine wave
        const waveY =
          y + amplitude * Math.sin(t * Math.PI * 2 * completeCycles);
        points.push(`${x},${waveY}`);
      }

      // Always end exactly at the baseline for smooth transition to vertical line
      points.push(`${endX},${y}`);

      // Create SVG path from points
      return `M ${points.join(" L ")}`;
    };

    // Pause phase segment
    if (therapyParams.enabledPhases.includes(TherapyPhase.PAUSE)) {
      const pauseWidth =
        (therapyParams.pauseDuration / therapyParams.cycleDuration) *
        graphWidth;
      const startX = currentX;
      const endX = startX + pauseWidth;
      const y = normalizeY(therapyParams.pausePressure);

      // Special case: If inspTriggerStatus is 1 or 2 display a cleaner triangle bump pattern for insp trig and coughCue enabled or just insp Trig enabled {0:all disabled, 1:both enabled, 2:insp trig enabled, 3:coughCue enabled}
      if ([1, 2].includes(therapyParams.inspTriggerStatus)) {
        // Define fixed dimensions for dash pattern for consistency
        const fixedDashLength = 10; // Fixed 10px dash length
        const fixedDashSpacing = 10; // Reduced spacing between dashes (10px instead of 15px)
        const dashCycle = fixedDashLength + fixedDashSpacing; // Total length of one dash + space

        // Calculate segments for the trigger visualization
        let segmentStartX = startX;

        // Ensure we have at least one dash before the triangle
        // We'll use a fixed initial segment that accommodates at least one dash
        const initialDashCount = 1; // Guarantee at least one dash
        const initialSegmentWidth = initialDashCount * dashCycle;

        // Add exactly one dash before the triangle
        for (let i = 0; i < initialDashCount; i++) {
          const dashStartX = segmentStartX + i * dashCycle;
          segments.push({
            path: `M ${dashStartX},${y} L ${dashStartX + fixedDashLength},${y}`,
            color: phaseColors.pause,
            strokeWidth: 3,
          });
        }

        // Update current position after initial dashes
        segmentStartX += initialSegmentWidth;

        // Second: Add a continuous triangle bump (no baseline)
        // Triangle dimensions
        const triangleHeight = graphHeight * 0.04; // 4% of graph height
        const triangleWidth = pauseWidth * 0.1; // 10% of pause width

        // Calculate triangle points
        const triangleStartX = segmentStartX;
        const trianglePeakX = triangleStartX + triangleWidth / 2;
        const triangleEndX = triangleStartX + triangleWidth;

        // Use a single path command to draw the entire triangle to ensure continuity
        // Instead of drawing two separate lines, draw a single continuous path
        segments.push({
          path: `M ${triangleStartX},${y} L ${trianglePeakX},${
            y - triangleHeight
          } L ${triangleEndX},${y}`,
          color: phaseColors.pause,
          strokeWidth: 3,
        });

        // Update current position after the triangle
        segmentStartX += triangleWidth;

        // Third: Add the remaining dashes for the rest of the pause phase
        const remainingWidth = endX - segmentStartX;

        // Calculate how many complete dash cycles fit in the remaining segment
        const numRemainingDashes = Math.floor(remainingWidth / dashCycle);

        // Add remaining broken line segments with fixed dash length
        for (let i = 0; i < numRemainingDashes; i++) {
          const dashStartX = segmentStartX + i * dashCycle;
          segments.push({
            path: `M ${dashStartX},${y} L ${dashStartX + fixedDashLength},${y}`,
            color: phaseColors.pause,
            strokeWidth: 3,
          });
        }

        // Handle any remaining space with a final dash (can be longer if needed)
        const remainingFinalSpace =
          remainingWidth - numRemainingDashes * dashCycle;
        if (remainingFinalSpace >= 5) {
          // Only add final dash if there's at least 5px space
          const finalDashStartX =
            segmentStartX + numRemainingDashes * dashCycle;
          const finalDashLength = Math.min(
            remainingFinalSpace,
            fixedDashLength
          );
          segments.push({
            path: `M ${finalDashStartX},${y} L ${
              finalDashStartX + finalDashLength
            },${y}`,
            color: phaseColors.pause,
            strokeWidth: 3,
          });
        }

        // Position the text with left padding similar to the original implementation
        // Use a position that's left-shifted relative to the triangle peak
        const textOffsetFromTriangleStart = triangleWidth * 0.75; // Position text 3/4 of the way through the triangle
        const triggerTextX = triangleStartX + textOffsetFromTriangleStart;
        const triggerTextY = y - triangleHeight - 15; // Position above the peak

        therapyParams.triggerTextPosition = {
          x: triggerTextX,
          y: triggerTextY,
        };
      } else {
        // Check oscStatus to determine if we should use sine wave for pause phase
        const shouldUseSineWave =
          therapyParams.oscStatus === 1 || therapyParams.oscStatus === 2;

        if (shouldUseSineWave) {
          // Generate sine wave path
          segments.push({
            path: generateSineWavePath(startX, endX, y, "pause"),
            color: phaseColors.pause,
            strokeWidth: 3,
          });
        } else {
          // Standard straight line
          segments.push({
            path: `M ${startX},${y} L ${endX},${y}`,
            color: phaseColors.pause,
            strokeWidth: 3,
          });
        }
      }

      currentX = endX;
    }

    // Transition from Pause to Inspiratory with smooth connection
    if (
      therapyParams.enabledPhases.includes(TherapyPhase.PAUSE) &&
      therapyParams.enabledPhases.includes(TherapyPhase.INSPIRATORY)
    ) {
      const startY = normalizeY(therapyParams.pausePressure);
      const endY = normalizeY(therapyParams.inspPressure);

      // Create smooth transition by drawing a single path that includes
      // a small portion of the horizontal segments on both sides
      const transitionLength = 2; // Small overlap for smoothness

      segments.push({
        path: `M ${
          currentX - transitionLength
        },${startY} L ${currentX},${startY} L ${currentX},${endY} L ${
          currentX + transitionLength
        },${endY}`,
        color: phaseColors.inspiratory,
        strokeWidth: 3,
      });
    }

    // Inspiratory phase segment
    if (therapyParams.enabledPhases.includes(TherapyPhase.INSPIRATORY)) {
      const inspWidth =
        (therapyParams.inspDuration / therapyParams.cycleDuration) * graphWidth;

      // If no pause phase, set the starting point
      if (!therapyParams.enabledPhases.includes(TherapyPhase.PAUSE)) {
        currentX = padding;
      }

      const startX = currentX;
      const endX = startX + inspWidth;
      const y = normalizeY(therapyParams.inspPressure);

      // Special case: If inspTriggerStatus is 1 or 3, display dotted line with triangle at end
      if ([1, 3].includes(therapyParams.inspTriggerStatus)) {
        // Define fixed dimensions for dash pattern for consistency
        const fixedDashLength = 10; // Fixed 10px dash length
        const fixedDashSpacing = 10; // Reduced spacing between dashes (10px instead of 15px)
        const dashCycle = fixedDashLength + fixedDashSpacing; // Total length of one dash + space

        // Triangle dimensions (same as pause phase)
        const triangleHeight = graphHeight * 0.04; // 4% of graph height
        const triangleWidth = inspWidth * 0.1; // 10% of inspiratory width

        // Calculate the total width available for dashes (excluding triangle at the end)
        const dashingWidth = inspWidth - triangleWidth;

        // Calculate how many complete dash cycles fit in the dashing segment
        const numDashes = Math.floor(dashingWidth / dashCycle);

        // Add dashed line segments for most of the inspiratory phase
        for (let i = 0; i < numDashes; i++) {
          const dashStartX = startX + i * dashCycle;
          segments.push({
            path: `M ${dashStartX},${y} L ${dashStartX + fixedDashLength},${y}`,
            color: phaseColors.inspiratory,
            strokeWidth: 3,
          });
        }

        // Handle any remaining space before the triangle with a final dash
        const remainingSpace = dashingWidth - numDashes * dashCycle;
        if (remainingSpace >= 5) {
          // Only add final dash if there's at least 5px space
          const finalDashStartX = startX + numDashes * dashCycle;
          const finalDashLength = Math.min(remainingSpace, fixedDashLength);
          segments.push({
            path: `M ${finalDashStartX},${y} L ${
              finalDashStartX + finalDashLength
            },${y}`,
            color: phaseColors.inspiratory,
            strokeWidth: 3,
          });
        }

        // Add triangle at the END of the inspiratory phase
        const triangleStartX = endX - triangleWidth;
        const trianglePeakX = triangleStartX + triangleWidth / 2;
        const triangleEndX = endX;

        // Use a single path command to draw the entire triangle
        segments.push({
          path: `M ${triangleStartX},${y} L ${trianglePeakX},${
            y - triangleHeight
          } L ${triangleEndX},${y}`,
          color: phaseColors.inspiratory,
          strokeWidth: 3,
        });

        // Position the "CoughCue ON" text above the triangle at the end
        const textOffsetFromTriangleStart = triangleWidth * 0.5; // Center text over triangle
        const coughCueTextX = triangleStartX + textOffsetFromTriangleStart;
        const coughCueTextY = y - triangleHeight - 15; // Position above the peak

        // Store cough cue text position (you might want to add this to therapyParams)
        therapyParams.coughCueTextPosition = {
          x: coughCueTextX,
          y: coughCueTextY,
        };
      } else {
        // Check oscStatus to determine if we should use sine wave for inspiratory phase
        const shouldUseSineWave =
          therapyParams.oscStatus === 1 || therapyParams.oscStatus === 2;

        if (shouldUseSineWave) {
          // Generate sine wave path
          segments.push({
            path: generateSineWavePath(startX, endX, y, "inspiratory"),
            color: phaseColors.inspiratory,
            strokeWidth: 3,
          });
        } else {
          // Standard straight line
          segments.push({
            path: `M ${startX},${y} L ${endX},${y}`,
            color: phaseColors.inspiratory,
            strokeWidth: 3,
          });
        }
      }

      currentX = endX;
    }

    // Transition from Inspiratory to Expiratory with smooth connection
    if (
      therapyParams.enabledPhases.includes(TherapyPhase.INSPIRATORY) &&
      therapyParams.enabledPhases.includes(TherapyPhase.EXPIRATORY)
    ) {
      const startY = normalizeY(therapyParams.inspPressure);
      const endY = normalizeY(therapyParams.expPressure);
      const zeroY = normalizeY(0);
      const transitionLength = 2; // Small overlap for smoothness

      // MODIFIED: Split the line at y=0 if the expiratory pressure is negative
      if (therapyParams.expPressure < 0) {
        // Blue line from inspiratory to zero with smooth connections
        segments.push({
          path: `M ${
            currentX - transitionLength
          },${startY} L ${currentX},${startY} L ${currentX},${zeroY}`,
          color: phaseColors.inspiratory, // Use inspiratory color from insp to zero
          strokeWidth: 3,
        });

        // Yellow/orange line from zero to expiratory with smooth connections
        segments.push({
          path: `M ${currentX},${zeroY} L ${currentX},${endY} L ${
            currentX + transitionLength
          },${endY}`,
          color: phaseColors.expiratory, // Use expiratory color from zero to exp
          strokeWidth: 3,
        });
      } else {
        // If expiratory pressure is positive, use inspiratory color for entire transition
        segments.push({
          path: `M ${
            currentX - transitionLength
          },${startY} L ${currentX},${startY} L ${currentX},${endY} L ${
            currentX + transitionLength
          },${endY}`,
          color: phaseColors.inspiratory,
          strokeWidth: 3,
        });
      }
    }

    // Transition from Pause to Expiratory with smooth connection when no Inspiratory
    if (
      therapyParams.enabledPhases.includes(TherapyPhase.PAUSE) &&
      !therapyParams.enabledPhases.includes(TherapyPhase.INSPIRATORY) &&
      therapyParams.enabledPhases.includes(TherapyPhase.EXPIRATORY)
    ) {
      const startY = normalizeY(therapyParams.pausePressure);
      const endY = normalizeY(therapyParams.expPressure);
      const zeroY = normalizeY(0);
      const transitionLength = 2; // Small overlap for smoothness

      // MODIFIED: Split the line at y=0 if the expiratory pressure is negative
      if (therapyParams.expPressure < 0) {
        // Blue line from inspiratory to zero with smooth connections
        segments.push({
          path: `M ${
            currentX - transitionLength
          },${startY} L ${currentX},${startY} L ${currentX},${zeroY}`,
          color: phaseColors.pause, // Use inspiratory color from insp to zero
          strokeWidth: 3,
        });

        // Yellow/orange line from zero to expiratory with smooth connections
        segments.push({
          path: `M ${currentX},${zeroY} L ${currentX},${endY} L ${
            currentX + transitionLength
          },${endY}`,
          color: phaseColors.expiratory, // Use expiratory color from zero to exp
          strokeWidth: 3,
        });
      } else {
        // If expiratory pressure is positive, use inspiratory color for entire transition
        segments.push({
          path: `M ${
            currentX - transitionLength
          },${startY} L ${currentX},${startY} L ${currentX},${endY} L ${
            currentX + transitionLength
          },${endY}`,
          color: phaseColors.inspiratory,
          strokeWidth: 3,
        });
      }
    }

    // Expiratory phase segment
    if (therapyParams.enabledPhases.includes(TherapyPhase.EXPIRATORY)) {
      const expWidth =
        (therapyParams.expDuration / therapyParams.cycleDuration) * graphWidth;

      // If no prior phases, set the starting point
      if (
        !therapyParams.enabledPhases.includes(TherapyPhase.PAUSE) &&
        !therapyParams.enabledPhases.includes(TherapyPhase.INSPIRATORY)
      ) {
        currentX = padding;
      }

      const startX = currentX;
      const endX = startX + expWidth;
      const y = normalizeY(therapyParams.expPressure);

      // Check oscStatus to determine if we should use sine wave for expiratory phase
      const shouldUseSineWave =
        therapyParams.oscStatus === 1 || therapyParams.oscStatus === 3;

      if (shouldUseSineWave) {
        // Generate sine wave path
        segments.push({
          path: generateSineWavePath(startX, endX, y, "expiratory"),
          color: phaseColors.expiratory,
          strokeWidth: 3,
        });
      } else {
        // Standard straight line
        segments.push({
          path: `M ${startX},${y} L ${endX},${y}`,
          color: phaseColors.expiratory,
          strokeWidth: 3,
        });
      }
    }

    return segments;
  };

  // Also update the preview waveform segments to include the oscillation effects
  const getPreviewWaveformSegments = () => {
    const padding = 20;
    const graphWidth = dimensions.width - padding * 2;
    const graphHeight = dimensions.height - padding * 2;

    // If no phases are enabled or dimensions not set, return empty array
    if (therapyParams.enabledPhases.length === 0 || dimensions.width === 0) {
      return [];
    }

    // Use the calculated pressure range for normalization
    const minPressure = pressureRange.min;
    const maxPressure = pressureRange.max;
    const rangeMagnitude = maxPressure - minPressure;

    const normalizeY = (pressure: number) => {
      // Invert Y so higher pressure is higher on the graph
      return (
        dimensions.height -
        padding -
        ((pressure - minPressure) / rangeMagnitude) * graphHeight
      );
    };

    // Revert preview sine wave to match the regular version
    const generatePreviewSineWavePath = (
      startX: number,
      endX: number,
      y: number,
      phase: string
    ) => {
      // Calculate wave parameters
      const segmentWidth = endX - startX;

      // Define fixed wavelength in pixels
      const fixedWavelength = 15; // 30 pixels per complete wave cycle

      // Calculate how many complete waves we can fit
      const completeCycles = Math.floor(segmentWidth / fixedWavelength);

      // If segment is too small for even one complete wave, draw a straight line
      if (completeCycles < 1) {
        return `M ${startX},${y} L ${endX},${y}`;
      }

      // Define amplitude as a percentage of the graph height (small but noticeable)
      const amplitude = graphHeight * 0.025; // 2.5% of graph height

      // Adjust the actual segment we'll draw waves in to ensure we only draw complete waves
      const adjustedWidth = completeCycles * fixedWavelength;
      const remainingSpace = segmentWidth - adjustedWidth;

      // Center the waves within the available space by adding padding on both sides
      const startPadding = remainingSpace / 2;
      const adjustedStartX = startX + startPadding;

      // Calculate points along the sine wave
      // Use high point density for smooth curves (20 points per wave)
      const pointsPerCycle = 20;
      const pointCount = completeCycles * pointsPerCycle;

      const points = [];

      // Always start exactly at the baseline for smooth transition from vertical line
      points.push(`${startX},${y}`);

      // Generate the sine wave points
      for (let i = 0; i <= pointCount; i++) {
        const t = i / pointCount; // Normalized position (0 to 1)
        const x = adjustedStartX + adjustedWidth * t;
        // Calculate sine wave y position - use regular sine wave
        const waveY =
          y + amplitude * Math.sin(t * Math.PI * 2 * completeCycles);
        points.push(`${x},${waveY}`);
      }

      // Always end exactly at the baseline for smooth transition to vertical line
      points.push(`${endX},${y}`);

      // Create SVG path from points
      return `M ${points.join(" L ")}`;
    };

    const segments = [];
    let currentX = padding;

    // Pause phase horizontal segment
    if (therapyParams.enabledPhases.includes(TherapyPhase.PAUSE)) {
      const pauseWidth =
        (therapyParams.pauseDuration / therapyParams.cycleDuration) *
        graphWidth;
      const startX = currentX;
      const endX = startX + pauseWidth;
      const y = normalizeY(therapyParams.pausePressure);

      // Special case: Always use straight line for preview when inspTriggerStatus includes pause trigger
      if ([1, 2].includes(therapyParams.inspTriggerStatus)) {
        segments.push({
          path: `M ${startX},${y} L ${endX},${y}`,
          color: mutedPhaseColors.pause,
          strokeWidth: 2,
          strokeDasharray: "5,3",
        });
      } else {
        // Check oscStatus to determine if we should use sine wave for pause phase preview
        const shouldUseSineWave =
          therapyParams.oscStatus === 1 || therapyParams.oscStatus === 2;

        if (shouldUseSineWave) {
          // Generate sine wave path with dashed style
          segments.push({
            path: generatePreviewSineWavePath(startX, endX, y, "pause"),
            color: mutedPhaseColors.pause,
            strokeWidth: 2,
            strokeDasharray: "5,3",
          });
        } else {
          // Standard straight dashed line
          segments.push({
            path: `M ${startX},${y} L ${endX},${y}`,
            color: mutedPhaseColors.pause,
            strokeWidth: 2,
            strokeDasharray: "5,3",
          });
        }
      }

      currentX = endX;
    }

    // Transition from Pause to Inspiratory (vertical line) - always straight
    if (
      therapyParams.enabledPhases.includes(TherapyPhase.PAUSE) &&
      therapyParams.enabledPhases.includes(TherapyPhase.INSPIRATORY)
    ) {
      const startY = normalizeY(therapyParams.pausePressure);
      const endY = normalizeY(therapyParams.inspPressure);

      segments.push({
        path: `M ${currentX},${startY} L ${currentX},${endY}`,
        color: mutedPhaseColors.inspiratory,
        strokeWidth: 2,
        strokeDasharray: "5,3",
      });
    }

    // Inspiratory phase horizontal segment
    if (therapyParams.enabledPhases.includes(TherapyPhase.INSPIRATORY)) {
      const inspWidth =
        (therapyParams.inspDuration / therapyParams.cycleDuration) * graphWidth;

      // If no pause phase, set the starting point
      if (!therapyParams.enabledPhases.includes(TherapyPhase.PAUSE)) {
        currentX = padding;
      }

      const startX = currentX;
      const endX = startX + inspWidth;
      const y = normalizeY(therapyParams.inspPressure);

      // Special case: Always use straight line for preview when inspTriggerStatus includes cough cue
      if ([1, 3].includes(therapyParams.inspTriggerStatus)) {
        segments.push({
          path: `M ${startX},${y} L ${endX},${y}`,
          color: mutedPhaseColors.inspiratory,
          strokeWidth: 2,
          strokeDasharray: "5,3",
        });
      } else {
        // Check oscStatus to determine if we should use sine wave for inspiratory phase preview
        const shouldUseSineWave =
          therapyParams.oscStatus === 1 || therapyParams.oscStatus === 2;

        if (shouldUseSineWave) {
          // Generate sine wave path with dashed style
          segments.push({
            path: generatePreviewSineWavePath(startX, endX, y, "inspiratory"),
            color: mutedPhaseColors.inspiratory,
            strokeWidth: 2,
            strokeDasharray: "5,3",
          });
        } else {
          // Standard straight dashed line
          segments.push({
            path: `M ${startX},${y} L ${endX},${y}`,
            color: mutedPhaseColors.inspiratory,
            strokeWidth: 2,
            strokeDasharray: "5,3",
          });
        }
      }

      currentX = endX;
    }

    // Transition from Inspiratory to Expiratory (vertical line) - always straight
    if (
      therapyParams.enabledPhases.includes(TherapyPhase.INSPIRATORY) &&
      therapyParams.enabledPhases.includes(TherapyPhase.EXPIRATORY)
    ) {
      const startY = normalizeY(therapyParams.inspPressure);
      const endY = normalizeY(therapyParams.expPressure);
      const zeroY = normalizeY(0);

      // Split the line at y=0 if the expiratory pressure is negative
      if (therapyParams.expPressure < 0) {
        segments.push({
          path: `M ${currentX},${startY} L ${currentX},${zeroY}`,
          color: mutedPhaseColors.inspiratory,
          strokeWidth: 2,
          strokeDasharray: "5,3",
        });

        segments.push({
          path: `M ${currentX},${zeroY} L ${currentX},${endY}`,
          color: mutedPhaseColors.expiratory,
          strokeWidth: 2,
          strokeDasharray: "5,3",
        });
      } else {
        segments.push({
          path: `M ${currentX},${startY} L ${currentX},${endY}`,
          color: mutedPhaseColors.inspiratory,
          strokeWidth: 2,
          strokeDasharray: "5,3",
        });
      }
    }

    // Expiratory phase horizontal segment
    if (therapyParams.enabledPhases.includes(TherapyPhase.EXPIRATORY)) {
      const expWidth =
        (therapyParams.expDuration / therapyParams.cycleDuration) * graphWidth;

      // If no prior phases, set the starting point
      if (
        !therapyParams.enabledPhases.includes(TherapyPhase.PAUSE) &&
        !therapyParams.enabledPhases.includes(TherapyPhase.INSPIRATORY)
      ) {
        currentX = padding;
      }

      const startX = currentX;
      const endX = startX + expWidth;
      const y = normalizeY(therapyParams.expPressure);

      // Check oscStatus to determine if we should use sine wave for expiratory phase preview
      const shouldUseSineWave =
        therapyParams.oscStatus === 1 || therapyParams.oscStatus === 3;

      if (shouldUseSineWave) {
        // Generate sine wave path with dashed style
        segments.push({
          path: generatePreviewSineWavePath(startX, endX, y, "expiratory"),
          color: mutedPhaseColors.expiratory,
          strokeWidth: 2,
          strokeDasharray: "5,3",
        });
      } else {
        // Standard straight dashed line
        segments.push({
          path: `M ${startX},${y} L ${endX},${y}`,
          color: mutedPhaseColors.expiratory,
          strokeWidth: 2,
          strokeDasharray: "5,3",
        });
      }
    }

    return segments;
  };

  // Function to get current indicator position using the adaptive pressure range
  const getIndicatorPosition = () => {
    const padding = 20;
    const graphWidth = dimensions.width - padding * 2;
    const graphHeight = dimensions.height - padding * 2;

    // If no phases are enabled or dimensions not set, return default position
    if (
      therapyParams.enabledPhases.length === 0 ||
      dimensions.width === 0 ||
      currentPhase === TherapyPhase.INACTIVE
    ) {
      return { x: padding, y: dimensions.height - padding };
    }

    // Use the calculated pressure range for normalization
    const minPressure = pressureRange.min;
    const maxPressure = pressureRange.max;
    const rangeMagnitude = maxPressure - minPressure;

    const normalizeY = (pressure: number) => {
      // Invert Y so higher pressure is higher on the graph
      return (
        dimensions.height -
        padding -
        ((pressure - minPressure) / rangeMagnitude) * graphHeight
      );
    };

    let x = padding;
    let y = dimensions.height - padding;

    // Calculate the x position based on the current phase and progress
    switch (currentPhase) {
      case TherapyPhase.PAUSE: {
        // For pause phase: start at beginning, move across the pause section
        const pauseWidth =
          (therapyParams.pauseDuration / therapyParams.cycleDuration) *
          graphWidth;
        x = padding + phaseProgress * pauseWidth;
        y = normalizeY(therapyParams.pausePressure);
        break;
      }
      case TherapyPhase.INSPIRATORY: {
        // For inspiratory phase: start after pause section, move across insp section
        let inspStartX = padding;
        if (therapyParams.enabledPhases.includes(TherapyPhase.PAUSE)) {
          inspStartX +=
            (therapyParams.pauseDuration / therapyParams.cycleDuration) *
            graphWidth;
        }

        const inspWidth =
          (therapyParams.inspDuration / therapyParams.cycleDuration) *
          graphWidth;
        x = inspStartX + phaseProgress * inspWidth;
        y = normalizeY(therapyParams.inspPressure);
        break;
      }
      case TherapyPhase.EXPIRATORY: {
        // For expiratory phase: start after pause+insp sections, move across exp section
        let expStartX = padding;
        if (therapyParams.enabledPhases.includes(TherapyPhase.PAUSE)) {
          expStartX +=
            (therapyParams.pauseDuration / therapyParams.cycleDuration) *
            graphWidth;
        }
        if (therapyParams.enabledPhases.includes(TherapyPhase.INSPIRATORY)) {
          expStartX +=
            (therapyParams.inspDuration / therapyParams.cycleDuration) *
            graphWidth;
        }

        const expWidth =
          (therapyParams.expDuration / therapyParams.cycleDuration) *
          graphWidth;
        x = expStartX + phaseProgress * expWidth;
        y = normalizeY(therapyParams.expPressure);
        break;
      }
    }

    return { x, y };
  };

  // Function to generate Y-axis labels for specific pressure values only
  const generateYAxisLabels = () => {
    const padding = 20;
    const graphHeight = dimensions.height - padding * 2;

    // Use the calculated pressure range
    const min = pressureRange.min;
    const max = pressureRange.max;

    const labels: {
      value: number;
      y: number;
      color: string;
      isZero: boolean;
    }[] = [];
    const addedValues = new Set(); // Track values to avoid duplicates

    // Helper function to add a label
    const addLabel = (value: number, color: string, isZero = false) => {
      if (addedValues.has(value)) return; // Skip if already added

      const normalizedValue = (value - min) / (max - min);
      const y = dimensions.height - padding - normalizedValue * graphHeight;

      labels.push({
        value: value,
        y: y,
        color: color,
        isZero: isZero,
      });

      addedValues.add(value);
    };

    // Always add 0 first (if it's in range)
    if (min <= 0 && max >= 0) {
      addLabel(0, "#fff", true);
    }

    // Add therapy pressure values with their phase colors
    if (therapyParams.enabledPhases.includes(TherapyPhase.PAUSE)) {
      addLabel(therapyParams.pausePressure, "#66bb6a");
    }

    if (therapyParams.enabledPhases.includes(TherapyPhase.INSPIRATORY)) {
      addLabel(therapyParams.inspPressure, "#29b6f6");
    }

    if (therapyParams.enabledPhases.includes(TherapyPhase.EXPIRATORY)) {
      addLabel(therapyParams.expPressure, "#ffa726");
    }

    // Add min and max values (only if they're not already added as therapy pressures)
    addLabel(min, "#aaa");
    addLabel(max, "#aaa");

    // Sort labels by value to maintain proper order
    labels.sort((a, b) => a.value - b.value);

    return labels;
  };

  // Calculate positions for phase timers
  const getPhaseTimerPositions = () => {
    const padding = 20;
    const graphWidth = dimensions.width - padding * 2;

    const positions = {
      pause: { x: 0, width: 0, centerX: 0 },
      inspiratory: { x: 0, width: 0, centerX: 0 },
      expiratory: { x: 0, width: 0, centerX: 0 },
    };

    // Calculate pause phase position
    if (therapyParams.enabledPhases.includes(TherapyPhase.PAUSE)) {
      const pauseWidth =
        (therapyParams.pauseDuration / therapyParams.cycleDuration) *
        graphWidth;
      positions.pause.x = padding;
      positions.pause.width = pauseWidth;
      positions.pause.centerX = positions.pause.x + pauseWidth / 2;
    }

    // Calculate inspiratory phase position
    if (therapyParams.enabledPhases.includes(TherapyPhase.INSPIRATORY)) {
      let inspX = padding;
      if (therapyParams.enabledPhases.includes(TherapyPhase.PAUSE)) {
        inspX += positions.pause.width;
      }

      const inspWidth =
        (therapyParams.inspDuration / therapyParams.cycleDuration) * graphWidth;
      positions.inspiratory.x = inspX;
      positions.inspiratory.width = inspWidth;
      positions.inspiratory.centerX = positions.inspiratory.x + inspWidth / 2;
    }

    // Calculate expiratory phase position
    if (therapyParams.enabledPhases.includes(TherapyPhase.EXPIRATORY)) {
      let expX = padding;
      if (therapyParams.enabledPhases.includes(TherapyPhase.PAUSE)) {
        expX += positions.pause.width;
      }
      if (therapyParams.enabledPhases.includes(TherapyPhase.INSPIRATORY)) {
        expX += positions.inspiratory.width;
      }

      const expWidth =
        (therapyParams.expDuration / therapyParams.cycleDuration) * graphWidth;
      positions.expiratory.x = expX;
      positions.expiratory.width = expWidth;
      positions.expiratory.centerX = positions.expiratory.x + expWidth / 2;
    }

    return positions;
  };

  // Get the current values
  const waveformSegments = getWaveformSegments();
  const previewWaveformSegments = getPreviewWaveformSegments();
  const indicatorPos = getIndicatorPosition();
  const yAxisLabels = generateYAxisLabels();
  const phaseTimerPositions = getPhaseTimerPositions();

  return (
    <div
      className="wave-graph-container"
      ref={graphContainerRef}
      style={{ width: dimensions.width, height: dimensions.height }}
    >
      <AnimatePresence mode="wait">
      {!isConnected ? (
        <motion.div
        className="graph-disconnected"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        key="disconnected"
        >
        <IonText color="medium">Connect to device to view graph</IonText>
        </motion.div>
      ) : (
        <motion.div
        className="graph-active"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        key="active"
        >
        <svg width="100%" height="100%" className="wave-graph">
          {/* Grid and axis lines */}
          <line
          x1="20"
          y1={dimensions.height - 20}
          x2={dimensions.width - 20}
          y2={dimensions.height - 20}
          stroke="#555"
          strokeWidth="1"
          />
          <line
          x1="20"
          y1="20"
          x2="20"
          y2={dimensions.height - 20}
          stroke="#555"
          strokeWidth="1"
          />

          {/* Y-axis labels for specific pressure values only, prevent overlap */}
          {(() => {
          // Minimum vertical distance between labels (in px)
          const minLabelGap = 18;
          const lastY = -Infinity;
          // Priority: 0 > phase pressures > min/max
          // Sort labels: 0 first, then by distance from 0
          const sortedLabels = [...yAxisLabels].sort((a, b) => {
            if (a.isZero) return -1;
            if (b.isZero) return 1;
            // phase pressures next (colored, not gray)
            const aPhase = a.color !== "#aaa";
            const bPhase = b.color !== "#aaa";
            if (aPhase && !bPhase) return -1;
            if (!aPhase && bPhase) return 1;
            // then by closeness to 0
            return Math.abs(a.value) - Math.abs(b.value);
          });

          // Track which y's are used
          const usedYs: number[] = [];

          return sortedLabels.map((label, index) => {
            // Find if this label would overlap with any used y
            const overlaps = usedYs.some(
            (y) => Math.abs(y - label.y) < minLabelGap
            );
            // Always show 0, hide others if overlap
            const showLabel = label.isZero || !overlaps;
            if (showLabel) usedYs.push(label.y);

            return (
            <React.Fragment key={`y-label-${index}`}>
              {showLabel && (
              <text
                x="15"
                y={label.y}
                textAnchor="end"
                dominantBaseline="middle"
                fill={label.color}
                fontSize={label.isZero ? "10" : "11"}
                fontWeight={label.isZero ? "bold" : "normal"}
              >
                {label.value}
              </text>
              )}
              {/* Only show horizontal gridline for 0 */}
              {label.isZero && (
              <line
                x1="20"
                y1={label.y}
                x2={dimensions.width - 20}
                y2={label.y}
                stroke="#555"
                strokeWidth="0.7"
              />
              )}
            </React.Fragment>
            );
          });
          })()}

          {/* Phase background highlights */}
          {therapyParams.enabledPhases.includes(TherapyPhase.PAUSE) && (
          <motion.rect
            x={20}
            y={20}
            width={
            (therapyParams.pauseDuration /
              therapyParams.cycleDuration) *
            (dimensions.width - 40)
            }
            height={dimensions.height - 40}
            fill="#66bb6a"
            opacity={currentPhase === TherapyPhase.PAUSE ? 0.1 : 0.05}
            initial={false}
            animate={{
            opacity: currentPhase === TherapyPhase.PAUSE ? 0.1 : 0.05,
            }}
          />
          )}

          {therapyParams.enabledPhases.includes(
          TherapyPhase.INSPIRATORY
          ) && (
          <motion.rect
            x={
            20 +
            (therapyParams.enabledPhases.includes(TherapyPhase.PAUSE)
              ? (therapyParams.pauseDuration /
                therapyParams.cycleDuration) *
              (dimensions.width - 40)
              : 0)
            }
            y={20}
            width={
            (therapyParams.inspDuration / therapyParams.cycleDuration) *
            (dimensions.width - 40)
            }
            height={dimensions.height - 40}
            fill="#29b6f6"
            opacity={
            currentPhase === TherapyPhase.INSPIRATORY ? 0.1 : 0.05
            }
            initial={false}
            animate={{
            opacity:
              currentPhase === TherapyPhase.INSPIRATORY ? 0.1 : 0.05,
            }}
          />
          )}

          {therapyParams.enabledPhases.includes(
          TherapyPhase.EXPIRATORY
          ) && (
          <motion.rect
            x={
            20 +
            (therapyParams.enabledPhases.includes(TherapyPhase.PAUSE)
              ? (therapyParams.pauseDuration /
                therapyParams.cycleDuration) *
              (dimensions.width - 40)
              : 0) +
            (therapyParams.enabledPhases.includes(
              TherapyPhase.INSPIRATORY
            )
              ? (therapyParams.inspDuration /
                therapyParams.cycleDuration) *
              (dimensions.width - 40)
              : 0)
            }
            y={20}
            width={
            (therapyParams.expDuration / therapyParams.cycleDuration) *
            (dimensions.width - 40)
            }
            height={dimensions.height - 40}
            fill="#ffa726"
            opacity={
            currentPhase === TherapyPhase.EXPIRATORY ? 0.1 : 0.05
            }
            initial={false}
            animate={{
            opacity:
              currentPhase === TherapyPhase.EXPIRATORY ? 0.1 : 0.05,
            }}
          />
          )}

          {/* Waveform segments with phase-specific colors */}
          {waveformSegments.map((segment, index) => (
          <path
            key={`segment-${index}`}
            d={segment.path}
            fill="none"
            stroke={segment.color}
            strokeWidth={segment.strokeWidth}
          />
          ))}

          {/* Add "Insp. Trigger On" text when in pause phase and trigger is active */}
          {[1, 2].includes(therapyParams.inspTriggerStatus) &&
          currentPhase === TherapyPhase.PAUSE &&
          therapyParams.triggerTextPosition && (
            <text
            x={therapyParams.triggerTextPosition.x}
            y={therapyParams.triggerTextPosition.y}
            textAnchor="middle"
            fill="#66bb6a"
            fontSize="12"
            fontWeight="bold"
            className="insp-trigger-text"
            >
            Insp. Trigger On
            </text>
          )}

          {/* Add "CoughCue ON" text when in inspiratory phase and cough cue is active */}
          {[1, 3].includes(therapyParams.inspTriggerStatus) &&
          currentPhase === TherapyPhase.INSPIRATORY &&
          therapyParams.coughCueTextPosition && (
            <text
            x={therapyParams.coughCueTextPosition.x}
            y={therapyParams.coughCueTextPosition.y}
            textAnchor="middle"
            fill="#29b6f6"
            fontSize="12"
            fontWeight="bold"
            className="cough-cue-text"
            >
            CoughCue ON
            </text>
          )}

          {/* Indicator dot - ONLY shown when therapy is active */}
          {therapyActive && showIndicator && (
          <motion.circle
            cx={indicatorPos.x}
            cy={indicatorPos.y}
            r="8"
            initial={false}
            animate={{
            cx: indicatorPos.x,
            cy: indicatorPos.y,
            fill:
              currentPhase === TherapyPhase.PAUSE
              ? "#66bb6a"
              : currentPhase === TherapyPhase.INSPIRATORY
              ? "#29b6f6"
              : currentPhase === TherapyPhase.EXPIRATORY
              ? "#ffa726"
              : "#aaaaaa",
            }}
            transition={{
            type: "spring",
            stiffness: 300,
            damping: 30,
            }}
          />
          )}

          {/* Animated pulse around indicator - ONLY shown when therapy is active */}
          {therapyActive && showIndicator && (
          <motion.circle
            cx={indicatorPos.x}
            cy={indicatorPos.y}
            r="8"
            fill="transparent"
            stroke={
            currentPhase === TherapyPhase.PAUSE
              ? "#66bb6a"
              : currentPhase === TherapyPhase.INSPIRATORY
              ? "#29b6f6"
              : currentPhase === TherapyPhase.EXPIRATORY
              ? "#ffa726"
              : "#aaaaaa"
            }
            strokeWidth="2"
            initial={false}
            animate={{
            cx: indicatorPos.x,
            cy: indicatorPos.y,
            r: [8, 16, 8],
            opacity: [0.8, 0, 0.8],
            }}
            transition={{
            r: { repeat: Infinity, duration: 1.5 },
            opacity: { repeat: Infinity, duration: 1.5 },
            cx: { type: "spring", stiffness: 300, damping: 30 },
            cy: { type: "spring", stiffness: 300, damping: 30 },
            }}
          />
          )}
        </svg>

            {/* Phase timers */}
            {/* <div className="phase-timers">
            {therapyParams.enabledPhases.includes(TherapyPhase.PAUSE) && (
              <div className="timer-container" style={{ left: phaseTimerPositions.pause.centerX }}>
                <CircularTimer
                  progress={currentPhase === TherapyPhase.PAUSE ? 1 - phaseProgress : 0}
                  isActive={currentPhase === TherapyPhase.PAUSE}
                  color="#66bb6a"
                  label="Pause"
                  duration={therapyParams.pauseDuration}
                />
              </div>
            )}
            
            {therapyParams.enabledPhases.includes(TherapyPhase.INSPIRATORY) && (
              <div className="timer-container" style={{ left: phaseTimerPositions.inspiratory.centerX }}>
                <CircularTimer
                  progress={currentPhase === TherapyPhase.INSPIRATORY ? 1 - phaseProgress : 0}
                  isActive={currentPhase === TherapyPhase.INSPIRATORY}
                  color="#29b6f6"
                  label="Insp"
                  duration={therapyParams.inspDuration}
                />
              </div>
            )}
            
            {therapyParams.enabledPhases.includes(TherapyPhase.EXPIRATORY) && (
              <div className="timer-container" style={{ left: phaseTimerPositions.expiratory.centerX }}>
                <CircularTimer
                  progress={currentPhase === TherapyPhase.EXPIRATORY ? 1 - phaseProgress : 0}
                  isActive={currentPhase === TherapyPhase.EXPIRATORY}
                  color="#ffa726"
                  label="Exp"
                  duration={therapyParams.expDuration}
                />
              </div>
            )}
          </div> */}

        {/* Add an instruction message when therapy is not active */}
        {!therapyActive && (
          <div className="therapy-inactive-message">
          <IonText color="medium">
            Tap the play button to start therapy
          </IonText>
          </div>
        )}

        {/* Current phase indicator - moved down. Web: extra bottom offset. */}
        <div
          className={`current-phase${IS_WEB_BUILD ? " current-phase--web" : ""}`}
          style={{ marginTop: "15px" }}
        >
          <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          key={currentPhase}
          transition={{ type: "spring", stiffness: 500, damping: 30 }}
          >
          <IonText>
            <span className={`phase-name ${currentPhase}`}>
            {currentPhase.charAt(0).toUpperCase() +
              currentPhase.slice(1)}{" "}
            Phase
            </span>
          </IonText>
          </motion.div>
        </div>
        </motion.div>
      )}
      </AnimatePresence>
    </div>
  );
};

// Interface for TherapyDisplay props
interface TherapyDisplayProps {
  width: number;
  height: number;
}

// Main component that manages the state and combines the graph and pressure display
const TherapyDisplay: React.FC<TherapyDisplayProps> = ({ width, height }) => {
  const {
    isConnected,
    therapyState,
    therapyParams,
    currentPhase,
    phaseProgress,
  } = useBLE();

  // useEffect(() => {
  //   console.log(
  //     "therapy params",
  //     therapyParams,
  //     "currentPhase",
  //     currentPhase,
  //     "phaseProg",
  //     phaseProgress
  //   );
  // }, [therapyParams]);

  return (
    therapyParams && (
      <div className="therapy-display">
        <WaveGraph
          isConnected={isConnected}
          therapyActive={therapyState}
          currentPhase={currentPhase as TherapyPhase}
          phaseProgress={phaseProgress}
          therapyParams={therapyParams}
          width={width}
          height={height}
        />
      </div>
    )
  );
};

export default TherapyDisplay;
